<template>
  <div class="row">
    <div class="col-12">
      <div class="card shadow-sm">
        <DetailHeader :headerTitle="'이벤트 상세'"/>

        <div class="pt-3 pb-5 d-flex justify-content-center">

          <div v-if="loading">
            <TableBodyLoader :minHeight="615" :offsetY="-50" message="상세정보를 불러오는 중..." />
          </div>

          <div v-else class="w-100 px-5">
            <!-- Event (열 맞춤 + 줄바꿈) -->
            <div class="log-section mb-3">
              <div class="log-title">Event</div>
              <div class="log-kv">
                <div v-for="(row,i) in eventRows" :key="'e'+i" class="kv-row">
                  <div class="k">{{ row[0] }}</div>
                  <div class="v">{{ row[1] ?? '-' }}</div>
                </div>
              </div>
            </div>

            <!-- Input EPCs -->
            <div class="log-section mb-3">
              <div class="log-title">Input EPCs ({{ inputEPCs.length }})</div>
              <div v-if="inputEPCs.length" class="code-list">
                <div class="code-row" v-for="(epc,i) in inputEPCs" :key="'in'+i">
                  <div class="code">{{ epc }}</div>
                </div>
              </div>
              <div v-else class="log-section">
                <pre class="log-pre">None.</pre>
              </div>
            </div>

            <!-- Output EPCs -->
            <div class="log-section mb-3">
              <div class="log-title">Output EPCs ({{ outputEPCs.length }})</div>
              <div v-if="outputEPCs.length" class="code-list">
                <div class="code-row" v-for="(epc,i) in outputEPCs" :key="'out'+i">
                  <div class="code">{{ epc }}</div>
                </div>
              </div>
              <div v-else class="log-section">
                <pre class="log-pre">None.</pre>
              </div>
            </div>

            <!-- Raw JSON (기존 스타일 유지: 스크롤) -->
            <div class="log-section">
              <div class="log-title">Raw JSON</div>
              <pre class="log-pre">{{ pretty(raw) }}</pre>
            </div>

          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import DetailHeader from "@/components/DetailHeader.vue";
import TableBodyLoader from "@/components/loading/TableBodyLoader.vue";
import axios from "axios";

export default {
  name: "EventDetailLogs",
  components: { DetailHeader, TableBodyLoader },
  data() {
    return {
      loading: false,
      raw: null,
      evt: {},
      inputEPCs: [],
      outputEPCs: [],
    };
  },
  computed: {
    eventId() {
      return decodeURIComponent(this.$route.params.id);
    },
    // ✅ Event 섹션에 그리드로 뿌릴 (키, 값)
    eventRows() {
      const e = this.evt || {};
      return [
        ['TYPE',         e.type],
        ['EVENT ID',     e.eventID],
        ['EVENT TIME',   this.fmt(e.eventTime)],
        ['RECORD TIME',  this.fmt(e.recordTime)],
        ['TIMEZONE',     e.eventTimeZoneOffset],
        ['BIZSTEP',      e.bizStep],
        ['DISPOSITION',  e.disposition],
        ['READ POINT',   e.readPoint?.id],
        ['BIZ LOCATION', e.bizLocation?.id],
        ['COMP PREFIX',  e['cbv:extension']?.companyPrefix],
      ];
    },
  },
  async mounted() {
    await this.fetchDetail();
  },
  methods: {
    async fetchDetail(minMs = 500) {
      this.loading = true;
      try {
        const [res] = await Promise.all([
          axios.get(`/api/epcisAPI/events`, {
            params: { paths: `events/${this.eventId}` }
          }),
          new Promise(r => setTimeout(r, minMs)),
        ]);

        const data = res?.data;
        this.raw = data;
        const e = data?.epcisBody?.queryResults?.resultsBody?.eventList?.[0] || {};
        this.evt = e;
        this.inputEPCs = e.inputEPCList || [];
        this.outputEPCs = e.outputEPCList || [];
      } catch (e) {
        this.toastError?.("상세 조회 중 오류가 발생했습니다.");
      } finally {
        this.loading = false;
      }
    },
    fmt(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      const p = n => String(n).padStart(2,'0');
      return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    },
    pretty(obj) {
      try { return JSON.stringify(obj, null, 2); } catch { return String(obj ?? ''); }
    },
  }
};
</script>

<style scoped>
.card { border: 1px solid rgba(0,0,0,.06) !important; }

.log-section { background: #fff; overflow: hidden; }
.log-title { font-weight: 700; padding: .5rem .1rem; color: #344767; }

/* ✅ 키/값 그리드: 열 맞춤 + 줄바꿈 */
.log-kv {
  background: #0b1020;
  color: #f9fafb;
  border-radius: .5rem;
  padding: 1rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 13px;
  line-height: 1.55;
}

.kv-row {
  display: grid;
  grid-template-columns: 200px 1fr; /* 왼쪽 키 고정폭 (필요시 조정) */
  column-gap: 12px;
  align-items: start;
  padding: .15rem 0;
}

.k { color: #aeb7d0; white-space: nowrap; }   /* 키는 한 줄 유지 */
.v { white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word; }

/* EPC 코드 리스트 */
.code-list {
  background: #0b1020;
  color: #f9fafb;
  border-radius: .5rem;
  padding: 10px 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 13px;
  line-height: 1.55;
  max-height: 360px;          /* 길면 스크롤 */
  overflow: auto;
}
.code-row + .code-row { margin-top: 6px; }
.code {
  display: block;
  white-space: pre-wrap;       /* 줄바꿈 허용 */
  overflow-wrap: anywhere;     /* 긴 토큰 강제 개행 */
  word-break: break-word;
}

/* Raw JSON: 스크롤 유지 */
.log-pre {
  border-radius: .5rem;
  margin: 0;
  padding: 1rem;
  background: #0b1020;
  color: #f9fafb;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre;            /* JSON 포매팅 보존 */
  overflow: auto;
  max-height: 1000px;
}

/* 반응형: 화면 좁아지면 키 폭 조정 */
@media (max-width: 992px) { .kv-row { grid-template-columns: 170px 1fr; } }
@media (max-width: 768px) { .kv-row { grid-template-columns: 140px 1fr; } }
@media (max-width: 576px) { .kv-row { grid-template-columns: 120px 1fr; } }
</style>
